/**
 * PageHeader — the single sanctioned page header for WeaveIn pages.
 *
 * Anatomy (mirrors Triveny's TvPageBar, Tailwind-native per the Triveny
 * barrel's "WeaveIn pages should NOT import Tv*" rule):
 *
 *   eyebrow  (mono uppercase micro-label, "Area · Subsection")
 *   title    (Inter Tight 18 / 600 / -0.015em)
 *   description (optional 13px ink-500 subtitle)
 *   actions  (right-aligned buttons)
 *   tabs     (optional sub-nav row: active = 2px accent underline)
 *
 * Under a tab strip (TabbedChrome) the tab names the page, so the eyebrow and
 * title are dropped and only the page's own controls remain.
 *
 * Pages should render exactly one PageHeader and no other h1/h2 page title.
 * See docs/WEAVEIN_UI_STANDARDS.md.
 */
import React from 'react'
import { Eyebrow } from './Eyebrow'
import { useEmbeddedChrome } from './EmbeddedChrome'
import { useTabbedChrome } from './TabbedChrome'

export interface PageHeaderTab {
  key: string
  label: React.ReactNode
  /** Optional count badge rendered after the label. */
  count?: number
}

interface PageHeaderProps {
  /** Optional leading element before the title block (e.g. a Back button). */
  leading?: React.ReactNode
  /** "Area · Subsection" micro-label above the title. */
  eyebrow?: React.ReactNode
  /** Page title — rendered as the page's h1. */
  title: React.ReactNode
  /** Optional one-line subtitle under the title. */
  description?: React.ReactNode
  /** Right-aligned action area. */
  actions?: React.ReactNode
  /** Optional sub-nav tabs rendered as a second row. */
  tabs?: PageHeaderTab[]
  activeTab?: string
  onTabChange?: (key: string) => void
  /** Extra content below the title block (e.g. filter chips). */
  children?: React.ReactNode
  className?: string
}

export function PageHeader({
  leading,
  eyebrow,
  title,
  description,
  actions,
  tabs,
  activeTab,
  onTabChange,
  children,
  className = '',
}: PageHeaderProps) {
  const embedded = useEmbeddedChrome()
  const tabbed = useTabbedChrome()

  if (tabbed && !embedded) {
    // The tab strip already names the page — drop the eyebrow and title and keep
    // what the page uniquely owns. Nothing left means no bar at all.
    if (!leading && description === undefined && !actions && !children && (!tabs || tabs.length === 0)) return null
    title = null
    eyebrow = undefined
  }

  if (embedded) {
    // Host shell (e.g. HubShell) renders the page title — keep only what the
    // page uniquely owns: its actions and (rarely) its tab row.
    if (!actions && (!tabs || tabs.length === 0)) return null
    return (
      <div className="flex flex-wrap items-center gap-2 px-4 pt-4 sm:px-6">
        {tabs && tabs.length > 0 && (
          <nav className="flex gap-4" aria-label="Page tabs" role="tablist">
            {tabs.map((tab) => {
              const active = tab.key === activeTab
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  data-track-label={typeof tab.label === 'string' ? tab.label : undefined}
                  onClick={() => onTabChange?.(tab.key)}
                  className={`flex items-center gap-1.5 border-b-2 pb-1.5 font-display text-[13px] font-semibold transition-colors ${
                    active ? 'border-v2accent text-ink' : 'border-transparent text-ink-500 hover:text-ink'
                  }`}
                >
                  {tab.label}
                </button>
              )
            })}
          </nav>
        )}
        <div className="flex-1" />
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    )
  }

  return (
    <div className={`border-b border-v2border bg-surface ${className}`}>
      <div className="flex flex-wrap items-center gap-3 px-4 py-3.5 sm:px-6">
        {leading && <div className="flex flex-shrink-0 items-center">{leading}</div>}
        {(title || description !== undefined) && (
          <div className="min-w-0 flex-shrink">
            {eyebrow !== undefined && <Eyebrow className="truncate">{eyebrow}</Eyebrow>}
            {title && (
              <h1 className="truncate font-display text-lg font-semibold tracking-[-0.015em] text-ink">
                {title}
              </h1>
            )}
            {description !== undefined && (
              <p className={`${title ? 'mt-0.5' : ''} truncate text-[13px] text-ink-500`}>{description}</p>
            )}
          </div>
        )}
        <div className="flex-1" />
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
      {tabs && tabs.length > 0 && (
        <nav className="flex gap-4 px-4 sm:px-6" aria-label="Page tabs" role="tablist">
          {tabs.map((tab) => {
            const active = tab.key === activeTab
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={active}
                // Plain label for the activity tracker; the count badge is not part of it (TRUS-664).
                data-track-label={typeof tab.label === 'string' ? tab.label : undefined}
                onClick={() => onTabChange?.(tab.key)}
                className={`-mb-px flex items-center gap-1.5 border-b-2 pb-2.5 pt-1 font-display text-[13px] font-semibold transition-colors ${
                  active
                    ? 'border-v2accent text-ink'
                    : 'border-transparent text-ink-500 hover:text-ink'
                }`}
              >
                {tab.label}
                {tab.count !== undefined && (
                  <span
                    className={`inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 font-mono text-[10px] font-semibold ${
                      active ? 'bg-v2accent-soft text-v2accent-ink' : 'bg-paper-2 text-ink-500'
                    }`}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            )
          })}
        </nav>
      )}
    </div>
  )
}
